import { useState } from "react";

import { Typography, TextField, Button, InfoPanel } from "@king-ict/eupisi-ui/components";
import { ReactComponent as AddIcon } from "@king-ict/eupisi-ui/components/Icon/Add.svg";
import { ReactComponent as RemoveIcon } from "@king-ict/eupisi-ui/components/Icon/RemoveIcon.svg";
import { DocumentData } from "@king-ict/eupisi-ui/models";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { getUserFromOibRequest } from "../../api/nisooApi";
import { ButtonWithValidation } from "../../components/ButtonWithValidation";
import StaticField from "../../components/StaticField/StaticField";
import UploadButton from "../../components/UploadButton";
import { DocumentType } from "../../models/Document";
import { ProgramRequestTeacher, createProgramRequestTeacherInitial, createProgramRequestClassEnsambleInitial, TeacherLanguage } from "../../models/ProgramRequest"
import { isNonEmptyEnsamble, normalizeEnsambleValue } from "../../utils/isNonEmptyEnsamble";
import styles from "./ProgramRequestEdit.module.scss";
import TeacherLanguageInfo from "./TeacherLanguageInfo";

export interface TeacherInfoProps{
    name: string;
    teachers: ProgramRequestTeacher[];
    readonly?: boolean;
    showLanguages?: boolean;
    allowAdd?: boolean;
    onChange: (e: any) => void;
    getErrorForPath: (path: string) => string | undefined;
    doValidations: () => boolean;
}


export default function TeacherInfo({teachers, onChange, name, getErrorForPath, doValidations, readonly = false, showLanguages = false, allowAdd = false} : TeacherInfoProps):  JSX.Element{
    const { t } = useTranslation();

    const [userNotFound, setUserNotFound] = useState<boolean[]>([] as boolean[]);
    const [userFound, setUserFound] = useState<boolean[]>([] as boolean[]);

    const handleOibChange = (event: any, index: number) => {
        var newTeachersList = teachers.slice();
        newTeachersList[index].oib = event.target.value;
        newTeachersList[index].oibChecked = false;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newTeachersList
            }
        });        
    };

    const handleFirstNameChange = (event: any, index: number) => {
        var newTeachersList = teachers.slice();
        newTeachersList[index].firstName = event.target.value;
        newTeachersList[index].oibChecked = false;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newTeachersList
            }
        });
    };

    const handleLastNameChange = (event: any, index: number) => {
        var newTeachersList = teachers.slice();
        newTeachersList[index].lastName = event.target.value;
        newTeachersList[index].oibChecked = false;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newTeachersList
            }
        });
    };

    const handleDiplomaDocumentAdded = (event: any, index: number, file: File) => {        

        var diplomaDocument: DocumentData = {
            id: -1,
            fileName: file.name,
            file: file
        };
        var newTeachersList = teachers.slice();
        newTeachersList[index].diplomaDocument = diplomaDocument;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newTeachersList
            }
        });
    };

    const handleTeacherStatementAdded = (event: any, index: number, file: File) => {
        var statementDocument: DocumentData = {
            id: -1,
            fileName: file.name,
            file: file
        };
        var newTeachersList = teachers.slice();
        newTeachersList[index].statement = statementDocument;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newTeachersList
            }
        });
    };

    const handleTeacherAdditionalDocumentationAdded = (event: any, index: number, file: File) => {
        var additionalDocumentationDocument: DocumentData = {
            id: -1,
            fileName: file.name,
            file: file
        };
        var newTeachersList = teachers.slice();
        newTeachersList[index].additionalDocumentation = additionalDocumentationDocument;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newTeachersList
            }
        });
    };

    const handleDiplomaDocumentRemoved = (event: any, index: number) => {
        var newTeachersList = teachers.slice();
        newTeachersList[index].diplomaDocument = undefined;

        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newTeachersList
            }
        });
    };

    const handleTeacherStatementRemoved = (event: any, index: number) => {
        var newTeachersList = teachers.slice();
        newTeachersList[index].statement = undefined;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newTeachersList
            }
        });
    };

    const handleTeacherAdditionalDocumentationRemoved = (event: any, index: number) => {
        var newTeachersList = teachers.slice();
        newTeachersList[index].additionalDocumentation = undefined;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newTeachersList
            }
        });        
    };

    const handleEnsambleAdd = (event: any, index: number) => {        
        var newEnsambleList = teachers[index].programRequestClassEnsambles.slice();
        newEnsambleList.push(createProgramRequestClassEnsambleInitial());
        var newTeachersList = teachers.slice();
        newTeachersList[index].programRequestClassEnsambles = newEnsambleList;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newTeachersList
            }
        });
    };

    const handleEnsambleChange = (event: any, ensambleIndex: number, teacherIndex: number) => {
        var newEnsamblesList = teachers[teacherIndex].programRequestClassEnsambles.slice();
        newEnsamblesList[ensambleIndex].ensamble = normalizeEnsambleValue(event.target.value);
        var newTeachersList = teachers.slice();
        newTeachersList[teacherIndex].programRequestClassEnsambles = newEnsamblesList;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name, 
                value: newTeachersList
            }
        });
    };

    const handleEnsambleRemove = (event: any, ensambleIndex: number, teacherIndex: number) => {
        var newEnsamblesList = teachers[teacherIndex].programRequestClassEnsambles.slice();
        newEnsamblesList.splice(ensambleIndex, 1);
        var newTeachersList = teachers.slice();
        newTeachersList[teacherIndex].programRequestClassEnsambles = newEnsamblesList;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newTeachersList
            }
        });
    };

    const handleLanguageChange = (event: any, index: number) => {
        const teacherLanguages = event.target.value as TeacherLanguage[];
        const teacherNew: ProgramRequestTeacher = {
            ...teachers[index],
            languages: teacherLanguages,
        };
        teachers[index] = teacherNew;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name, 
                value: teachers,
            }
        });
    };

    const onCheckUserData = async(event: any, teacherIndex: number) => {        
        if(teachers[teacherIndex].oib.trim() != "" && teachers[teacherIndex].firstName.trim() != "" && teachers[teacherIndex].lastName.trim() != ""){

            var userInfo = await getUserFromOibRequest(teachers[teacherIndex].oib, teachers[teacherIndex].firstName, teachers[teacherIndex].lastName);

            if(userInfo && userInfo.firstName && userInfo.lastName){
                var userFoundList = userFound.slice();
                userFoundList[teacherIndex] = true;
                var userNotFoundList = userNotFound.slice();
                userNotFoundList[teacherIndex] = false;

                setUserFound(userFoundList);
                setUserNotFound(userNotFoundList);

                var newTeachersList = teachers.slice();
                newTeachersList[teacherIndex].firstName = userInfo.firstName;
                newTeachersList[teacherIndex].lastName = userInfo.lastName;
                newTeachersList[teacherIndex].oibChecked = true;

                onChange({
                    ...event,
                    target: {
                        ...event.target,
                        name: name,
                        value: newTeachersList
                    }
                });
                return;
            }  
        }
        var userFoundList = userFound.slice();
        userFoundList[teacherIndex] = false;
        var userNotFoundList = userNotFound.slice();
        userNotFoundList[teacherIndex] = true;

        setUserFound(userFoundList);
        setUserNotFound(userNotFoundList);      
    };

    const handleTeacherAdd = (event: any) => {        
        var userFoundList = userFound.slice();
        userFoundList.push(false);
        var userNotFoundList = userNotFound.slice();
        userNotFoundList.push(false);

        setUserFound(userFoundList);
        setUserNotFound(userNotFoundList);

        var newTeachersList = teachers.slice();
        newTeachersList.push(createProgramRequestTeacherInitial(showLanguages));
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newTeachersList
            }
        });
    };

    const handleTeacherRemove = (event: any, teacherIndex: number) => {
        var userFoundList = userFound.slice();
        userFoundList.splice(teacherIndex, 1);
        var userNotFoundList = userNotFound.slice();
        userNotFoundList.splice(teacherIndex, 1);

        setUserFound(userFoundList);
        setUserNotFound(userNotFoundList);

        var newTeachersList = teachers.slice();
        newTeachersList.splice(teacherIndex, 1);
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newTeachersList
            }
        });
    };

    return (
        <>
            <Row>
                <Col md={12} className={styles.rowTitleSpacing}>
                    <Typography variant="smallHeading" component="h3">
                        {t("request.teacherTitle")}                        
                    </Typography>
                </Col>
            </Row>
            
            {teachers.map((teacher, teacherIndex) => 
            {
                const effectiveReadonly = teacher.id !== 0 && readonly;
                const teacherKey = teacher.id !== 0 ? teacher.id : `new-${teacherIndex}`;
                const displayedEnsambles = readonly
                  ? (teacher.programRequestClassEnsambles ?? []).filter((ensamble) => isNonEmptyEnsamble(ensamble.ensamble))
                  : (teacher.programRequestClassEnsambles ?? []);
                return (
                <div key={teacherKey} className={teacherIndex !== 0 ? "mt-3" : ""}>
                    {!effectiveReadonly && userNotFound && userNotFound[teacherIndex] &&
                        <Row>
                            <Col md={12}>
                                <InfoPanel
                                    message={t("userRole.userNotFound", {oib: teachers[teacherIndex].oib.toString().padStart(11, "0")})}
                                    color="warning"
                                    fullWidth
                                />
                            </Col>
                        </Row>
                    }
                    {!effectiveReadonly && userFound && userFound[teacherIndex] &&
                        <Row>
                            <Col md={12}>
                                <InfoPanel
                                    message={t("request.userFound")}
                                    color="success"
                                    fullWidth
                                />
                            </Col>
                        </Row>

                    }
                    <div className={styles.card}>                        
                        <Row>
                            <Col md={3}>
                                <TextField
                                    label={t("request.teacherOIB")}
                                    name={nameof<typeof teacher>("oib")}
                                    value={teacher.oib}
                                    onChange={(e) => handleOibChange(e, teacherIndex)}
                                    errorText={getErrorForPath(`${name}${teacherIndex}.oib`)}
                                    plaintext={effectiveReadonly}
                                />
                            </Col>
                            <Col md={3}>
                                <TextField
                                    label={t("request.teacherFirstName")}
                                    name={nameof<typeof teacher>("firstName")}
                                    value={teacher.firstName}
                                    onChange={(e) => handleFirstNameChange(e, teacherIndex)}
                                    errorText={getErrorForPath(`${name}${teacherIndex}.firstName`)}
                                    plaintext={effectiveReadonly}
                                />
                            </Col>
                            <Col md={3}>
                                <TextField
                                    label={t("request.teacherLastName")}
                                    name={nameof<typeof teacher>("lastName")}
                                    value={teacher.lastName}
                                    onChange={(e) => handleLastNameChange(e, teacherIndex)}
                                    errorText={getErrorForPath(`${name}${teacherIndex}.lastName`)}
                                    plaintext={effectiveReadonly}
                                />
                            </Col>
                            {!effectiveReadonly &&
                            <Col md={3} className={styles.provjeraPodatakaContainer} >                         
                                <ButtonWithValidation
                                    outline
                                    className={styles.checkDataButton} 
                                    onClick={(e) => onCheckUserData(e, teacherIndex)}
                                    errorText={getErrorForPath(`${name}${teacherIndex}.oibChecked`)}
                                >
                                    {t("request.teacherCheck")}
                                </ButtonWithValidation>

                            </Col>
                            }
                        </Row>
                        <Row>
                            {!effectiveReadonly &&
                            <>
                            <Col md={3}>
                                <UploadButton                                                       
                                    label={t("request.teacherDiplomaLabel")}
                                    name={nameof<typeof teacher>("diplomaDocument")}
                                    value={teacher.diplomaDocument}                    
                                    documentType={DocumentType.TeacherDiploma}
                                    toolTip={t("document.imagesAndPdfTooltip")}
                                    fileFilter={t("document.imagesAndPdfFilter")}
                                    onFileAdded={(file, e) => handleDiplomaDocumentAdded(e, teacherIndex, file)}
                                    onFileRemoved={(e) => handleDiplomaDocumentRemoved(e, teacherIndex)}
                                    errorText={getErrorForPath(`${name}${teacherIndex}.diplomaDocument`)}
                                />
                            </Col>
                            <Col md={3}>
                                <UploadButton
                                    label={t("request.teacherStatementLabel")}
                                    name={nameof<typeof teacher>("statement")}
                                    value={teacher.statement}
                                    documentType={DocumentType.TeacherStatement}
                                    toolTip={t("document.imagesAndPdfTooltip")}
                                    fileFilter={t("document.imagesAndPdfFilter")}
                                    onFileAdded={(file, e) => handleTeacherStatementAdded(e, teacherIndex, file)}
                                    onFileRemoved={(e) => handleTeacherStatementRemoved(e, teacherIndex)}
                                    errorText={getErrorForPath(`${name}${teacherIndex}.statement`)}
                                />
                            </Col>
                            <Col md={3}>
                                <UploadButton    
                                    label={t("request.teacherDocumentationLabel")}                
                                    name={nameof<typeof teacher>("additionalDocumentation")}
                                    value={teacher.additionalDocumentation}
                                    documentType={DocumentType.TeacherAdditional}
                                    toolTip={t("document.imagesAndPdfTooltip")}
                                    fileFilter={t("document.imagesAndPdfFilter")}
                                    onFileAdded={(file, e) => handleTeacherAdditionalDocumentationAdded(e, teacherIndex, file)}
                                    onFileRemoved={(e) => handleTeacherAdditionalDocumentationRemoved(e, teacherIndex)}
                                    errorText={getErrorForPath(`${name}${teacherIndex}.additionalDocumentation`)}
                                />
                            </Col>
                            </>
                            }
                            {effectiveReadonly &&
                            <>
                            <Col md={3} className={styles.hideUploadButton}>
                                {teacher.diplomaDocument ? (
                                  <UploadButton
                                    label={t("request.teacherDiplomaLabel")}
                                    name={nameof<typeof teacher>("diplomaDocument")}
                                    value={teacher.diplomaDocument}
                                    disabled={true}
                                    documentType={DocumentType.TeacherDiploma}
                                  />
                                ) : (
                                  <StaticField
                                    label={t("request.teacherDiplomaLabel")}
                                    value=""
                                    className={styles.changedFieldModule}
                                  />
                                )}
                            </Col>
                            <Col md={3} className={styles.hideUploadButton}>
                                {teacher.statement ? (
                                  <UploadButton
                                    label={t("request.teacherStatementLabel")}
                                    name={nameof<typeof teacher>("statement")}
                                    value={teacher.statement}
                                    disabled={true}
                                    documentType={DocumentType.TeacherStatement}
                                  />
                                ) : (
                                  <StaticField
                                    label={t("request.teacherStatementLabel")}
                                    value=""
                                    className={styles.changedFieldModule}
                                  />
                                )}
                            </Col>
                            <Col md={3} className={styles.hideUploadButton}>
                                {teacher.additionalDocumentation ? (
                                  <UploadButton
                                    label={t("request.teacherDocumentationLabel")}
                                    name={nameof<typeof teacher>("additionalDocumentation")}
                                    value={teacher.additionalDocumentation}
                                    disabled={true}
                                    documentType={DocumentType.TeacherAdditional}
                                  />
                                ) : (
                                  <StaticField
                                    label={t("request.teacherDocumentationLabel")}
                                    value=""
                                    className={styles.changedFieldModule}
                                  />
                                )}
                            </Col>
                            </>
                            }
                        </Row>
                        <Row>
                            {displayedEnsambles.map((ensamble, ensambleIndex) => (
                                <Col
                                  key={ensamble.id !== 0 ? ensamble.id : `${teacherKey}-${ensambleIndex}`}
                                  md={12}
                                  className={"d-flex"}
                                >
                                  <TextField
                                    className={"w-100"}
                                    label={t("request.classEnsamble")}
                                    value={ensamble.ensamble}
                                    maxLength={500}
                                    onChange={(e) => handleEnsambleChange(e, ensambleIndex, teacherIndex)}
                                    errorText={getErrorForPath(
                                      `${name}${teacherIndex}.programRequestClassEnsambles${ensambleIndex}.ensamble`,
                                    )}
                                    plaintext={effectiveReadonly}
                                  />
                                  {!effectiveReadonly && (
                                    <div className={"pl-3"} style={{ marginTop: "26px" }}>
                                      <Button
                                        boxless
                                        disabled={teacher.programRequestClassEnsambles.length <= 1}
                                        onClick={(e) => handleEnsambleRemove(e, ensambleIndex, teacherIndex)}
                                      >
                                        <RemoveIcon />
                                      </Button>
                                    </div>
                                  )}
                                </Col>
                              ))}
                        </Row>
                        {(!effectiveReadonly) &&
                        <Row className={"text-right"}>
                            <Col md={12}>
                                <Button 
                                    outline
                                    icon={<AddIcon/>}
                                    onClick={(e) => handleEnsambleAdd(e, teacherIndex)}
                                >
                                    {t("request.addEnsamble")}
                                </Button>
                            </Col>
                        </Row>
                        }
                        {showLanguages &&
                        <Row>
                            <Col md={12}>
                                <TeacherLanguageInfo
                                    name={`${name}${teacherIndex}.languages`}
                                    teacherLanguages={teacher.languages ?? []}
                                    onChange={(e) => handleLanguageChange(e, teacherIndex)}
                                    readonly={!(!readonly || allowAdd)}
                                    getErrorForPath={getErrorForPath}
                                />
                            </Col>
                        </Row>
                        }
                        {!effectiveReadonly &&
                        <Row>
                            <Col md={12} className="text-right">
                                <Button
                                    className={`p-0 ${teacherIndex > 0 ? 'visible' : 'invisible'}`}
                                    boxless
                                    disabled={teachers.length <= 1}
                                    onClick={(e) => handleTeacherRemove(e, teacherIndex)}
                                >
                                    {t("request.removeTeacher")}
                                </Button>
                            </Col>
                        </Row>
                        }                 
                    </div>
                </div>
            )})}
            {(!readonly || allowAdd) &&
            <Row className={"text-right mt-3"}>            
                <Col md={12}>
                    <ButtonWithValidation
                        outline                        
                        icon={<AddIcon/>}
                        onClick={(e) => handleTeacherAdd(e)}                        
                        errorText={getErrorForPath(name)}
                    >
                        {t("request.addTeacher")}
                    </ButtonWithValidation>                    
                </Col>
            </Row>
            }
        </>
    );
};